import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check } from "lucide-react";

interface SignatureCaptureProps {
  label: string;
  onCapture: (dataUrl: string) => void;
  existingSignature?: string;
  disabled?: boolean;
}

export function SignatureCapture({ label, onCapture, existingSignature, disabled }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    return { canvas, ctx };
  }, []);

  // Setup canvas on mount
  useEffect(() => {
    const r = getCtx();
    if (!r) return;
    const { canvas, ctx } = r;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Use dark stroke that works in both light and dark mode
    // Fill canvas with white background first for dark mode visibility
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);

    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (existingSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasDrawn(true);
      };
      img.src = existingSignature;
    }
  }, [getCtx, existingSignature]);

  const getPos = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.TouchEvent | React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    const r = getCtx();
    if (!r) return;
    const pos = getPos(e);
    r.ctx.strokeStyle = "#1a1a2e";
    r.ctx.lineWidth = 2;
    r.ctx.lineCap = "round";
    r.ctx.lineJoin = "round";
    r.ctx.beginPath();
    r.ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const r = getCtx();
    if (!r) return;
    const pos = getPos(e);
    r.ctx.lineTo(pos.x, pos.y);
    r.ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const r = getCtx();
    if (!r) return;
    const rect = r.canvas.getBoundingClientRect();
    // Refill white background
    r.ctx.fillStyle = "#ffffff";
    r.ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
  };

  const confirm = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onCapture(canvas.toDataURL("image/png"));
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <div className={`relative rounded-lg border-2 border-dashed overflow-hidden ${disabled ? "border-muted bg-muted/30" : "border-primary/30"}`}>
        <canvas
          ref={canvasRef}
          className="w-full touch-none cursor-crosshair"
          style={{ height: 120, background: "#ffffff" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasDrawn && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-xs text-muted">Sign here</p>
          </div>
        )}
      </div>
      {!disabled && (
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={clear} disabled={!hasDrawn}>
            <Eraser className="h-3 w-3" /> Clear
          </Button>
          <Button type="button" size="sm" className="h-7 text-[10px] gap-1" onClick={confirm} disabled={!hasDrawn}>
            <Check className="h-3 w-3" /> Confirm Signature
          </Button>
        </div>
      )}
    </div>
  );
}
